/**
 * Render API Service
 * BYK-141: Multi-Turn Refinement with Thought Signatures
 *
 * Client-side API functions for AI-powered kitchen renders.
 * Uses Gemini 3 Pro via the backend for photorealistic image generation.
 *
 * BYK-141 additions:
 * - refineRender() - Create a refinement of an existing render
 * - getRenderLineage() - Get the full refinement chain
 */

import api from "./api";

export type RenderStyle = "photorealistic" | "sketch" | "blueprint" | "minimal";
export type RenderCamera = "front" | "corner" | "top-down" | "eye-level";
export type RenderLighting = "daylight" | "evening" | "studio" | "dramatic";
export type RenderStatus = "pending" | "processing" | "completed" | "failed";

/**
 * BYK-177: Reference image for multi-modal AI grounding
 */
export interface ReferenceImage {
  /** Base64-encoded image data (without data URL prefix) */
  data: string;
  /** MIME type */
  mimeType: "image/png" | "image/jpeg";
  /** Type of reference image */
  type: "floor_plan" | "white_model" | "combined";
  /** Optional description for AI context */
  description?: string;
}

export interface RenderRequest {
  projectId: string;
  style?: RenderStyle;
  cameraAngle?: RenderCamera;
  lightingMood?: RenderLighting;
  sync?: boolean; // If true, wait for completion
  enableKashrut?: boolean; // Apply Kashrut zone design rules
  // BYK-177: Reference images for multi-modal grounding
  floorPlanImage?: ReferenceImage;
  whiteModelImage?: ReferenceImage;
  combinedImage?: ReferenceImage;
}

export interface Render {
  id: string;
  r2Key: string;
  prompt: string | null;
  style: RenderStyle | null;
  status: RenderStatus;
  error: string | null;
  metadata: string | null;
  projectId: string;
  createdAt: string;
  updatedAt: string;
  // BYK-141: Multi-turn refinement fields
  thoughtSignature?: string | null;
  refinementDepth?: number;
  refinementPrompt?: string | null;
  parentRenderId?: string | null;
}

// BYK-141: Render with refinement metadata
export interface RenderWithRefinement extends Render {
  canRefine: boolean;
  imageUrl?: string;
}

// BYK-141: Refinement request
export interface RefineRenderRequest {
  refinementPrompt: string;
  style?: RenderStyle;
  cameraAngle?: RenderCamera;
  lightingMood?: RenderLighting;
}

// BYK-141: Lineage node for history view
export interface RenderLineageNode {
  id: string;
  refinementPrompt: string | null;
  refinementDepth: number;
  status: RenderStatus;
  createdAt: string;
  imageUrl?: string;
}

// BYK-141: Lineage response
export interface RenderLineageResponse {
  current: RenderLineageNode & { canRefine: boolean };
  ancestors: RenderLineageNode[];
  rootRenderId: string;
}

export interface RenderListResponse {
  items: Render[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateRenderResponse {
  render: Render;
  imageUrl?: string; // Only present for sync renders
}

// Backend response structure
interface BackendRenderResponse {
  data: Render & { imageUrl?: string };
}

/**
 * Create a new render for a project
 */
export async function createRender(request: RenderRequest): Promise<CreateRenderResponse> {
  const response = await api.post<BackendRenderResponse>("/api/renders", request);
  const { imageUrl, ...render } = response.data.data;
  return { render, imageUrl };
}

/**
 * List renders for a project with pagination
 */
export async function listRenders(params: {
  projectId?: string;
  status?: RenderStatus;
  limit?: number;
  offset?: number;
}): Promise<RenderListResponse> {
  const searchParams = new URLSearchParams();
  if (params.projectId) searchParams.set("projectId", params.projectId);
  if (params.status) searchParams.set("status", params.status);
  if (params.limit) searchParams.set("limit", String(params.limit));
  if (params.offset) searchParams.set("offset", String(params.offset));

  const query = searchParams.toString();
  const response = await api.get<RenderListResponse>(`/api/renders${query ? `?${query}` : ""}`);
  return response.data;
}

/**
 * Get a single render by ID
 */
export async function getRender(id: string): Promise<Render> {
  const response = await api.get<{ data: Render }>(`/api/renders/${id}`);
  return response.data.data;
}

/**
 * Get the image URL for a render
 */
export function getRenderImageUrl(renderId: string): string {
  const baseUrl = import.meta.env.VITE_API_URL || "http://localhost:4205";
  return `${baseUrl}/api/renders/${renderId}/image`;
}

/**
 * Delete a render
 */
export async function deleteRender(id: string): Promise<void> {
  await api.delete(`/api/renders/${id}`);
}

/**
 * Poll for render completion
 * Returns when render is complete or failed
 */
export async function waitForRender(
  renderId: string,
  options: {
    pollInterval?: number;
    maxAttempts?: number;
    onProgress?: (render: Render) => void;
  } = {}
): Promise<Render> {
  const { pollInterval = 2000, maxAttempts = 60, onProgress } = options;

  for (let i = 0; i < maxAttempts; i++) {
    const render = await getRender(renderId);

    if (onProgress) {
      onProgress(render);
    }

    if (render.status === "completed" || render.status === "failed") {
      return render;
    }

    await new Promise((resolve) => setTimeout(resolve, pollInterval));
  }

  throw new Error("Render timed out after " + (pollInterval * maxAttempts) / 1000 + " seconds");
}

/**
 * Style options for UI display
 */
export const RENDER_STYLES: { id: RenderStyle; label: string; description: string }[] = [
  {
    id: "photorealistic",
    label: "Photorealistic",
    description: "High-quality 4K render with realistic lighting and materials",
  },
  {
    id: "sketch",
    label: "Sketch",
    description: "Hand-drawn architectural sketch style",
  },
  {
    id: "blueprint",
    label: "Blueprint",
    description: "Technical blueprint with dimensions and annotations",
  },
  {
    id: "minimal",
    label: "Minimal",
    description: "Clean, minimal design visualization",
  },
];

export const RENDER_CAMERAS: { id: RenderCamera; label: string }[] = [
  { id: "eye-level", label: "Eye Level" },
  { id: "corner", label: "Corner View" },
  { id: "front", label: "Front View" },
  { id: "top-down", label: "Top Down" },
];

export const RENDER_LIGHTING: { id: RenderLighting; label: string }[] = [
  { id: "daylight", label: "Natural Daylight" },
  { id: "evening", label: "Evening Warm" },
  { id: "studio", label: "Studio" },
  { id: "dramatic", label: "Dramatic" },
];

// =============================================================================
// BYK-141: Multi-Turn Refinement API Functions
// =============================================================================

/** Maximum refinement iterations allowed */
export const MAX_REFINEMENT_DEPTH = 5;

/**
 * Refine an existing render with a new prompt
 * Creates a new render that builds on the previous one using thought signatures
 */
export async function refineRender(
  renderId: string,
  request: RefineRenderRequest
): Promise<RenderWithRefinement> {
  const response = await api.post<{ data: RenderWithRefinement }>(
    `/api/renders/${renderId}/refine`,
    request
  );
  return response.data.data;
}

/**
 * Get the full refinement lineage for a render
 * Returns the chain from root render to current, with all ancestors
 */
export async function getRenderLineage(renderId: string): Promise<RenderLineageResponse> {
  const response = await api.get<RenderLineageResponse>(`/api/renders/${renderId}/lineage`);
  return response.data;
}

/**
 * Check if a render can be refined
 * Must be completed and have a thought signature, and under max depth
 */
export function canRefineRender(render: Render): boolean {
  return (
    render.status === "completed" &&
    !!render.thoughtSignature &&
    (render.refinementDepth || 0) < MAX_REFINEMENT_DEPTH
  );
}

// =============================================================================
// BYK-177: Reference Image Helpers
// =============================================================================

/**
 * Convert a data URL to a ReferenceImage object for the API
 * @param dataUrl - Data URL (e.g., "data:image/png;base64,...")
 * @param type - Type of reference image
 * @param description - Optional description for AI context
 */
export function dataUrlToReferenceImage(
  dataUrl: string,
  type: ReferenceImage["type"],
  description?: string
): ReferenceImage {
  // Parse data URL format: data:[<mediatype>][;base64],<data>
  const matches = dataUrl.match(/^data:(image\/(?:png|jpeg));base64,(.+)$/);

  if (!matches) {
    throw new Error("Invalid data URL format. Expected image/png or image/jpeg base64 data URL.");
  }

  const [, mimeType, base64Data] = matches;

  return {
    data: base64Data,
    mimeType: mimeType as "image/png" | "image/jpeg",
    type,
    description,
  };
}

/**
 * Create a render request with reference images from canvas exports
 */
export interface CreateRenderWithReferencesOptions {
  projectId: string;
  style?: RenderStyle;
  cameraAngle?: RenderCamera;
  lightingMood?: RenderLighting;
  enableKashrut?: boolean;
  /** Data URL from 2D floor plan export */
  floorPlanDataUrl?: string;
  /** Data URL from 3D white model export */
  whiteModelDataUrl?: string;
  /** Data URL from combined export */
  combinedDataUrl?: string;
}

/**
 * Create a render with reference images
 * Convenience function that handles data URL conversion
 */
export async function createRenderWithReferences(
  options: CreateRenderWithReferencesOptions
): Promise<CreateRenderResponse> {
  const request: RenderRequest = {
    projectId: options.projectId,
    style: options.style,
    cameraAngle: options.cameraAngle,
    lightingMood: options.lightingMood,
    enableKashrut: options.enableKashrut,
    sync: true, // Always sync for renders with references
  };

  // Convert data URLs to reference images
  if (options.floorPlanDataUrl) {
    request.floorPlanImage = dataUrlToReferenceImage(
      options.floorPlanDataUrl,
      "floor_plan",
      "2D top-down floor plan showing kitchen layout with measurements in cm"
    );
  }

  if (options.whiteModelDataUrl) {
    request.whiteModelImage = dataUrlToReferenceImage(
      options.whiteModelDataUrl,
      "white_model",
      "3D white/gray model showing volumetric layout and spatial relationships"
    );
  }

  if (options.combinedDataUrl) {
    request.combinedImage = dataUrlToReferenceImage(
      options.combinedDataUrl,
      "combined",
      "Combined view: 2D floor plan (left) and 3D white model (right)"
    );
  }

  return createRender(request);
}
